import React, { useState, useEffect, useMemo, useContext } from 'react';
import { Box, Accordion, AccordionDetails, Typography } from '@material-ui/core';
import PropTypes from 'prop-types';
import StoreTimeElements from './StoreTimeElements.component';
import OmniTimeElements from './OmniTimeElements.component';
import { timeFrameStyle, StyledAccordionSummary } from './TimeFrame.style';
import { useSelector } from 'react-redux';
import { isCalFamily } from '../filter/FilterCondition';
import {
	BASIC,
	OMNI_TMPT_LIST,
	STR_TMPT_LIST,
	REGISTER_SALES_LIST,
	TIME,
	OTIF_TMPT_LIST,
	ECOM_SALES_LIST,
	MUMD_ORDER_LIST,
	ECOM_INVENTORY,
	CHARTER_ONLY_TAG,
	MTS_TEMPLATE_LIST_BASIC,
	MULTI_TIME_SELECTION,
	MTS_TEMPLATE_LIST_CHARTER,
	PURCHASE_ORDER_LIST,
	BOTTLE_DEPOSIT_IDS,
	TENDER_ANALYSIS_LIST,
	ECOMM_INSTOCK_LIST,
	HISTORICAL_REPORT_OMNI
} from 'constants/constant';
import { UserPortalContext } from 'context/appcontext';
import { FEATURES } from 'helper/basicFeatureFlag';
import { colors } from 'theme/color-palette';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { MultiTimeSelectionField } from '../multi-time-selection';
import { isCustomDataset } from 'helper/utils';

export default function TimeFrame({
	selectedTemplateData,
	shouldExpand,
	templateId,
	showCheckbox,
	cbHandleExpansion,
	mtsSubAttr
}) {
	const portalUserContext = useContext(UserPortalContext);
	const { customerType } = portalUserContext;
	const [usrSelAccr, setUsrSelAccr] = useState(null);
	const [count, setCount] = useState(0);
	const selectedList = useSelector((state) => state.previewReducer.selectedList);
	const usrFilterList = useSelector((state) => state.previewReducer.filterList);
	// eslint-disable-next-line prettier/prettier
	const { DISABLE_GREGORIAN_CALENDAR: disableGregorianCalendar } =
		FEATURES[customerType].PREVIEW_PAGE;

	useEffect(() => {
		const selDateObj = selectedList.find((el) => isCalFamily(el));
		const isDateFilterAvlb = usrFilterList?.find((el) => isCalFamily(el) && el.field !== undefined);
		if (selDateObj !== undefined) {
			const newUserSelAccr =
				selDateObj?.localName !== MULTI_TIME_SELECTION ? selDateObj.parent : MULTI_TIME_SELECTION;
			setUsrSelAccr(newUserSelAccr);
		} else if (isDateFilterAvlb !== undefined) {
			const newUserSelAccr =
				isDateFilterAvlb?.localName !== MULTI_TIME_SELECTION
					? isDateFilterAvlb.parent
					: MULTI_TIME_SELECTION;
			setUsrSelAccr(newUserSelAccr);
		}
	}, [selectedList, usrFilterList]);

	useEffect(() => {
		const noSel = selectedList.filter((el) => isCalFamily(el));
		const noFil = usrFilterList?.some((el) => isCalFamily(el) && el.field !== undefined);
		setCount(noSel?.length || 0);
		if (noSel?.length === 0 && !noFil) {
			setUsrSelAccr(null);
		}
	}, [selectedList, usrFilterList]);

	const classes = timeFrameStyle();

	const RenderTimeElements = ({ subAttr, accordianName, disabled }) => {
		const TMLT_LIST = [
			...STR_TMPT_LIST,
			...OTIF_TMPT_LIST,
			...OMNI_TMPT_LIST,
			...ECOM_SALES_LIST,
			...MUMD_ORDER_LIST,
			...ECOM_INVENTORY,
			...PURCHASE_ORDER_LIST,
			...BOTTLE_DEPOSIT_IDS,
			...TENDER_ANALYSIS_LIST,
			...ECOMM_INSTOCK_LIST,
			...REGISTER_SALES_LIST,
			...HISTORICAL_REPORT_OMNI
		];
		if (TMLT_LIST.includes(Number(templateId)) || isCustomDataset(templateId)) {
			return (
				<OmniTimeElements
					list={subAttr}
					accordianName={accordianName}
					cbAccordianClk={(name) => {
						setUsrSelAccr(name);
					}}
					disabled={disabled}
					templateId={templateId}
					showCheckbox={showCheckbox}
				/>
			);
		}

		return (
			<StoreTimeElements
				list={subAttr}
				accordianName={accordianName}
				templateId={templateId}
				showCheckbox={showCheckbox}
			/>
		);
	};

	const RenderDurationList = ({ item }) => {
		const isFilterAvlb = usrFilterList.some((el) => {
			if (isCalFamily(el)) {
				return el.parent !== item.businessName && el.field !== undefined;
			}
		});

		const disableGregCalendar =
			disableGregorianCalendar && item.businessName === 'Gregorian Calendar';
		const shouldDisabled = useMemo(() => {
			// For Basic user 'Gregorian Calendar' should be disabled
			if (disableGregCalendar) {
				return true;
			}
			return (usrSelAccr && item.businessName !== usrSelAccr) || isFilterAvlb;
		}, [usrSelAccr]);

		return (
			<>
				<Typography
					className={classes.subListItem}
					style={{ color: shouldDisabled ? colors.DISABLED : colors.FONT_COLOR }}>
					<span className={classes.listItemTextBussName}>{item.businessName}</span>
					{/* For Basic user show tag for 'Gregorian Calendar' */}
					{disableGregCalendar ? (
						<Typography className={classes.charterOnlyText}>{CHARTER_ONLY_TAG}</Typography>
					) : null}
				</Typography>
				<RenderTimeElements
					subAttr={item.subAttributes}
					accordianName={item.businessName}
					disabled={shouldDisabled}
				/>
			</>
		);
	};

	if (selectedTemplateData === undefined) {
		return null;
	}

	const MTSEnabledTemplates =
		customerType === BASIC ? MTS_TEMPLATE_LIST_BASIC : MTS_TEMPLATE_LIST_CHARTER;

	const getTotalCount = () => {
		let totalCount = 0;
		selectedTemplateData?.forEach((time) => (totalCount += time.subAttributes?.length));
		// If MTS is available under the Time Period, then the count should be added in the denominator
		if (MTSEnabledTemplates.includes(Number(templateId))) {
			totalCount = totalCount + 1;
		}
		return totalCount;
	};

	return (
		<Box className={classes.root}>
			<Accordion
				className={classes.accordian}
				expanded={shouldExpand()}
				onChange={() => cbHandleExpansion([TIME])}
				elevation={0}
				square={true}
				id="accordian-time">
				<StyledAccordionSummary aria-controls="panel1a-content" id="panel1a-header">
					{!shouldExpand() && <ArrowRightIcon sx={{ fontSize: 24 }} />}
					{shouldExpand() && <ArrowDropDownIcon sx={{ fontSize: 24 }} />}
					<Typography className={classes.listItemText}>
						<span className={classes.subHeaderText}>{TIME}</span>
						<span className={`${classes.subHeaderCount} ${classes.floatRight}`}>
							{`${count} / ${getTotalCount()}`}
						</span>
					</Typography>
				</StyledAccordionSummary>
				<AccordionDetails>
					{/*  Multi-time Selection */}
					{MTSEnabledTemplates.includes(Number(templateId)) && (
						<MultiTimeSelectionField
							mtsSubAttr={mtsSubAttr}
							parentName={selectedTemplateData?.[0]?.businessName}
						/>
					)}
					{selectedTemplateData?.map((item) => {
						return <RenderDurationList item={item} key={item?.businessName} />;
					})}
				</AccordionDetails>
			</Accordion>
		</Box>
	);
}

TimeFrame.propTypes = {
	selectedTemplateData: PropTypes.array.isRequired
};
